import { gql, useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import * as React from 'react';

import { Loading } from '@/imports/ui/chakra/components/Loading';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import { NotAllowedToViewContent } from '@/imports/ui/pages/group/NotAllowedToViewContent';

import {
  GroupResourcesPane_SingleLibraryFolderVersionFileLink,
  GroupResourcesPane_SingleLibraryFolderVersionFileLinkVariables,
} from './graphql-types/GroupResourcesPane_SingleLibraryFolderVersionFileLink';
import {
  GroupResourcesPane_groupQuery,
  GroupResourcesPane_groupQueryVariables,
} from './graphql-types/GroupResourcesPane_groupQuery';

import { GroupPageLayout } from '../GroupPageLayout';
import { GroupResourcesPaneContent } from './GroupResourcesPaneContent';

const GROUP_RESOURCE_PANE_QUERY = gql`
  query GroupResourcesPane_groupQuery($input: GroupInput!) {
    group(input: $input) {
      result {
        _id
        currentUserCanEditLibrary
        library {
          _id
          rootFolder {
            _id
          }
        }
        currentUserGroupPermission {
          canViewContent
        }
      }
    }
  }
`;

const SINGLE_LIBRARY_FOLDER_VERSION_FILE_LINK_QUERY = gql`
  query GroupResourcesPane_SingleLibraryFolderVersionFileLink(
    $input: SingleLibraryFolderVersionFileLinkInput!
  ) {
    singleLibraryFolderVersionFileLink(input: $input) {
      _id
      libraryFolderId
      versionFileId
      versionFile {
        ...GroupResourcesPaneContent_singleVersionFileFragment
      }
    }
  }
  ${GroupResourcesPaneContent.singleVersionFileFragment}
`;

export const GroupResourcesPane: React.FC = (props) => {
  const router = useRouter();
  const {
    groupId,
    versionFileId: urlVersionFileId,
    folderId: urlFolderId,
  } = router.query;

  const { loading, error, data } = useQuery<
    GroupResourcesPane_groupQuery,
    GroupResourcesPane_groupQueryVariables
  >(GROUP_RESOURCE_PANE_QUERY, {
    variables: { input: { _id: groupId.toString() } },
  });

  const group = data?.group.result;
  const library = group?.library;

  const singleVersionFileToFetch = urlVersionFileId?.toString();
  const { data: singleVFLData } = useQuery<
    GroupResourcesPane_SingleLibraryFolderVersionFileLink,
    GroupResourcesPane_SingleLibraryFolderVersionFileLinkVariables
  >(SINGLE_LIBRARY_FOLDER_VERSION_FILE_LINK_QUERY, {
    variables: {
      input: {
        groupId: group?._id || '',
        versionFileId: singleVersionFileToFetch,
      },
    },
    skip: !group?._id,
  });

  const singleVersionFileLink =
    singleVFLData?.singleLibraryFolderVersionFileLink;
  const initialFolderId =
    singleVersionFileLink &&
    singleVersionFileLink.libraryFolderId !== library?.rootFolder._id
      ? singleVersionFileLink.libraryFolderId
      : urlFolderId
      ? urlFolderId.toString()
      : library?.rootFolder._id;
  if (
    initialFolderId !== urlFolderId &&
    initialFolderId !== library?.rootFolder._id
  ) {
    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          folderId: initialFolderId,
        },
      },
      undefined,
      { shallow: true }
    );
  }
  return (
    <GroupPageLayout>
      {loading ? (
        <Loading />
      ) : error ? (
        <UnexpectedError />
      ) : !group ? (
        <UnexpectedError />
      ) : !group.currentUserGroupPermission?.canViewContent || !library ? (
        <NotAllowedToViewContent />
      ) : (
        <GroupResourcesPaneContent
          currentUserCanEditLibrary={group.currentUserCanEditLibrary}
          library={library}
          groupId={groupId.toString()}
          singleVersionFile={singleVersionFileLink?.versionFile}
          initialFolderId={initialFolderId || library.rootFolder._id}
        />
      )}
    </GroupPageLayout>
  );
};
